import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { useDebouncedValue } from './helpers';
import {
  findFunnelOption,
  getFunnelSelectionKey,
  normalizeFunnelLabel,
} from './funnels';
import { buildMeetingTitle, getMeetingDeadlineIso } from './payload';
import {
  useMeetingDeals,
  useMeetingFunnels,
  useMeetingLeads,
  useMeetingProjects,
  useMeetingUsers,
} from './queries';
import {
  DEFAULT_FUNNEL_LABEL,
  getDefaultMeetingSlotFormValues,
  meetingSlotFormSchema,
} from './types';
import type { FunnelKind, MeetingSlotFormValues } from './types';
import { useMeetingSlotFieldHandlers } from './useMeetingSlotFieldHandlers';
import { useSubmitMeetingSlot } from './useSubmitMeetingSlot';

type UseMeetingSlotFormParams = {
  selectedDate: Date;
  selectedSlot: string;
  selectedDuration: 30 | 60;
  onClose: () => void;
};

const resolver = zodResolver(
  meetingSlotFormSchema
) as unknown as import('react-hook-form').Resolver<MeetingSlotFormValues>;

export function useMeetingSlotForm({
  selectedDate,
  selectedSlot,
  selectedDuration,
  onClose,
}: UseMeetingSlotFormParams) {
  const [presenterOpened, setPresenterOpened] = useState(false);

  const formMethods = useForm<MeetingSlotFormValues>({
    resolver,
    defaultValues: getDefaultMeetingSlotFormValues(),
  });
  const { control, handleSubmit, setValue } = formMethods;

  const values = useWatch({ control });
  const formValues = {
    ...getDefaultMeetingSlotFormValues(),
    ...values,
  } as MeetingSlotFormValues;

  const debouncedProjectQuery = useDebouncedValue(formValues.projectQuery, 250);
  const debouncedLeadQuery = useDebouncedValue(formValues.leadQuery, 250);
  const debouncedDealQuery = useDebouncedValue(formValues.dealQuery, 250);
  const debouncedPresenterQuery = useDebouncedValue(
    formValues.presenterQuery,
    250
  );

  const { funnelOptions, isFetching: isFunnelsLoading } = useMeetingFunnels();

  const selectedFunnel = useMemo(
    () => findFunnelOption(funnelOptions, formValues.funnelLabel),
    [funnelOptions, formValues.funnelLabel]
  );

  const funnelKind: FunnelKind = selectedFunnel?.kind ?? 'lead';
  const selectedEntityTypeId = selectedFunnel?.entityTypeId ?? null;
  const selectedDealCategoryId = selectedFunnel?.dealCategoryId ?? null;
  const funnelSelectionKey = selectedFunnel
    ? getFunnelSelectionKey(selectedFunnel)
    : null;

  const {
    isFetching: isProjectsLoading,
    projectByTitle,
    projectOptions,
  } = useMeetingProjects(
    funnelKind === 'smart_process' ? selectedEntityTypeId : null,
    debouncedProjectQuery
  );

  const {
    isFetching: isLeadsLoading,
    leadByOption,
    leadOptions,
  } = useMeetingLeads(funnelKind === 'lead', debouncedLeadQuery);

  const {
    isFetching: isDealsLoading,
    dealByOption,
    dealOptions,
  } = useMeetingDeals(
    funnelKind === 'deal',
    selectedDealCategoryId,
    debouncedDealQuery
  );

  const {
    isFetching: isUsersLoading,
    presenterOptions,
    userByLabel,
  } = useMeetingUsers(presenterOpened, debouncedPresenterQuery);

  useEffect(() => {
    const normalizedLabel = normalizeFunnelLabel(formValues.funnelLabel);
    const hasOption = funnelOptions.some(
      (funnel) => funnel.label === normalizedLabel
    );

    if (!hasOption) {
      setValue('funnelLabel', DEFAULT_FUNNEL_LABEL);
      return;
    }

    if (normalizedLabel !== formValues.funnelLabel) {
      setValue('funnelLabel', normalizedLabel);
    }
  }, [formValues.funnelLabel, funnelOptions, setValue]);

  useEffect(() => {
    setValue('projectTitle', '');
    setValue('projectId', null);
    setValue('projectQuery', '');

    setValue('leadLabel', '');
    setValue('leadId', null);
    setValue('leadCrmRef', null);
    setValue('leadQuery', '');

    setValue('dealLabel', '');
    setValue('dealId', null);
    setValue('dealQuery', '');
  }, [funnelSelectionKey, setValue]);

  const title = useMemo(
    () => buildMeetingTitle(formValues, funnelKind),
    [formValues, funnelKind]
  );

  const deadlineISO = useMemo(
    () => getMeetingDeadlineIso(selectedDate, selectedSlot),
    [selectedDate, selectedSlot]
  );

  const fieldHandlers = useMeetingSlotFieldHandlers({
    dealByOption,
    leadByOption,
    projectByTitle,
    setPresenterOpened,
    setValue,
    userByLabel,
  });

  const { isSaving, submit } = useSubmitMeetingSlot({
    deadlineISO,
    funnelKind,
    onClose,
    selectedDuration,
    selectedEntityTypeId,
    selectedSlot,
    title,
  });

  return {
    control,
    formMethods,
    funnelKind,
    funnelOptions,
    ...fieldHandlers,
    handleSave: handleSubmit(submit),
    isDealsLoading,
    isFunnelsLoading,
    isLeadsLoading,
    isProjectsLoading,
    isSaving,
    isUsersLoading,
    dealOptions,
    leadOptions,
    presenterOptions,
    projectOptions,
  };
}

export type MeetingSlotFormState = ReturnType<typeof useMeetingSlotForm>;
